import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable, Injector, TemplateRef, Type } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MyOverlayRef } from './my-overlay-ref';
import { OverlayComponent } from './overlay/overlay.component';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class OverlayService {
  private _overlays = new BehaviorSubject<MyOverlayRef[]>([]);
  overlays$ = this._overlays.asObservable();

  private _overlayLastId = 1;
  private document = inject(DOCUMENT);
  constructor(private overlay: Overlay, private injector: Injector) {}

  open<R = any, T = any>(
    content: string | TemplateRef<any> | Type<any>,
    data: T,
    title = 'Overlay Title',
    config?: OverlayConfig
  ): MyOverlayRef<R> {
    const id = this._overlayLastId++;

    const configs = new OverlayConfig({
      hasBackdrop: false,
      ...config,
      panelClass: ['my-overlay', 'my-overlay-' + id]
    });

    const overlayRef = this.overlay.create(configs);

    const myOverlayRef = new MyOverlayRef<R, T>(id, title, overlayRef, content, data);
    const injector = this.createInjector(myOverlayRef);

    this._overlays.next([...this._overlays.value, myOverlayRef]);

    overlayRef.attach(new ComponentPortal(OverlayComponent, null, injector));

    const index = this._overlays.value.length - 1;
    myOverlayRef.afterClosed$.subscribe(() => {
      this._overlays.next([...this._overlays.value.slice(0, index), ...this._overlays.value.slice(index + 1)]);
    });

    return myOverlayRef;
  }

  close(id: number) {
    const overlay = this._overlays.value.find((o) => o.id === id);

    if (!overlay) return;

    overlay.overlay.detach();

    this._overlays.next(this._overlays.value.filter((v) => v.id !== id));
  }

  closeAll() {
    this._overlays.value.forEach((overlay) => {
      overlay.overlay.detach();
    });
    this._overlays.next([]);
  }

  createInjector(ref: MyOverlayRef) {
    return Injector.create({
      parent: this.injector,
      providers: [{ provide: MyOverlayRef, useValue: ref }]
    });
  }

  resetAll() {
    const offset = 15;
    this._overlays.value.forEach((overlay, index) => {
      overlay.updatePosition({
        x: offset * index,
        y: offset * index
      });
      overlay.moveToFront(this.document);
    });
  }
}
